/**
 * Generate HowTo schema for exercise
 * @param {Object} exercise - Exercise data
 * @param {Object} settings - General settings
 * @returns {Object|null} - Generated HowTo schema or null
 */
import { extractSentences, generateHowToSteps, generateHowToTools } from '@/utils/common';

export const generateExerciseHowToSchema = (exercise, siteIdentity) => {
  try {
    const { $localeRootPath } = useNuxtApp();
    const { t } = useI18n();

    if (!exercise || !exercise.title) {
      return null;
    }

    if (!siteIdentity) {
      return null;
    }

    if (!$localeRootPath || typeof $localeRootPath !== 'function') {
      return null;
    }

    if (!t || typeof t !== 'function') {
      return null;
    }

    const baseUrl = siteIdentity.url || 'https://blackroll.com';

    const exerciseUrl = `${baseUrl}${$localeRootPath('/exercises/' + exercise.objectID)}`;

    const description = exercise.shortDescription || exercise.longDescription || '';

    // Split description into sentences and create steps
    const sentences = extractSentences(description);

    if (sentences.length === 0) {
      return null;
    }

    // Generate steps from sentences
    const steps = generateHowToSteps(sentences, exercise.pngUrl, exercise.videoYoutubeId);

    // Generate tools from products
    const generateTools = () => generateHowToTools(exercise.products, baseUrl, $localeRootPath);

    // Generate total time from practice value
    const generateTotalTime = () => {
      if (!exercise.practiceValue) return null;
      const match = exercise.practiceValue.match(/(\d+)\s*Min/i);
      if (match && match[1]) {
        return `PT${match[1]}M`;
      }
      return null;
    };

    const schema = {
      '@type': 'HowTo',
      '@id': `${baseUrl}#exercise-${exercise.objectID}`,
      name: exercise.title,
      description: (exercise.shortDescription || exercise.longDescription || '').replace(/<[^>]*>/g, '').trim(),
      url: exerciseUrl,
      ...(exercise.pngUrl && {
        image: {
          '@type': 'ImageObject',
          url: exercise.pngUrl,
          description: `Image for ${exercise.title}`,
        },
      }),
      step: steps,
      ...(generateTools() && { tool: generateTools() }),
      ...(generateTotalTime() && { totalTime: generateTotalTime() }),
      author: {
        '@type': 'Organization',
        name: siteIdentity.name || 'BLACKROLL',
        url: baseUrl,
      },
      publisher: {
        '@type': 'Organization',
        name: siteIdentity.name || 'BLACKROLL',
        url: baseUrl,
        logo: {
          '@type': 'ImageObject',
          url: siteIdentity.logo || `${baseUrl}/favicon.ico`,
        },
      },
    };

    return schema;
  } catch (error) {
    return null;
  }
};
